import { Grid } from '@mui/material';
import React from 'react';
import { PersonSleepMetrics, PersonVitalControlLevel, PersonVitalTimeFrame, VitalSignsStatus, AggregatedTotalSleepData } from '@redux/person/personTypes';
import { formatMinutesToHoursAndMinutes } from 'utils/time';
import { getFixedPointDisplay } from 'utils/util';
import { GridRow } from '../GridRow/GridRow';
import style from './InsightsGrid.scss';

type Props = {
  patientMetrics?: PersonSleepMetrics;
  controlLevel?: PersonVitalControlLevel;
  containerClassName?: string;
  timeFrame?: PersonVitalTimeFrame;
  listOfAggregatedTotalSleepData?: AggregatedTotalSleepData[];
  hasChartData?: boolean;
};

const CONTROL_SLEEP_TIME_IN_MINUTES = 420;

export const SleepInsightsGrid: React.FC<Props> = ({ patientMetrics, listOfAggregatedTotalSleepData, controlLevel, containerClassName, timeFrame, hasChartData = true }) => {
  const getLabel = (label: string): string => {
    return timeFrame !== PersonVitalTimeFrame.TWENTY_FOUR_HOURS ? `Avg. ${label}` : label;
  };

  if (timeFrame === PersonVitalTimeFrame.TWELVE_MONTHS) {
    const distinctDaysByMonth = new Map<string, number>();
    for (const data of listOfAggregatedTotalSleepData ?? []) {
      distinctDaysByMonth.set(data.bucketLocal, data.distinctDays);
    }
    let totalDays = 0;
    for (const value of distinctDaysByMonth.values()) {
      totalDays += value;
    }
    if (totalDays > 0) {
      const avgSleepTime = Math.round((patientMetrics?.totalSleepTimeInMinutes ?? 0) / totalDays);
      const avgAwakeTime = Math.round((patientMetrics?.awakeTimeInMinutes ?? 0) / totalDays);
      const avgRemSleepTime = Math.round((patientMetrics?.remSleepTimeInMinutes ?? 0) / totalDays);
      const avgDeepSleepTime = Math.round((patientMetrics?.deepSleepTimeInMinutes ?? 0) / totalDays);
      const avgLightSleepTime = Math.round((patientMetrics?.lightSleepTimeInMinutes ?? 0) / totalDays);

      patientMetrics = {
        ...patientMetrics,
        totalSleepTimeInMinutes: avgSleepTime,
        awakeTimeInMinutes: avgAwakeTime,
        remSleepTimeInMinutes: avgRemSleepTime,
        deepSleepTimeInMinutes: avgDeepSleepTime,
        lightSleepTimeInMinutes: avgLightSleepTime
      };
    }
  }

  return (
    <Grid container className={containerClassName}>
      <GridRow label="" isHeaderRow metricValue="Patient metrics" normalControlValue="Control level" suboptimalControlValue="Suboptimal" abnormalControlValue="Abnormal" />
      <GridRow
        label={getLabel('Sleep Time (h)')}
        metricValue={hasChartData ? formatMinutesToHoursAndMinutes(patientMetrics?.totalSleepTimeInMinutes ?? 0) : '-'}
        normalControlValue={formatMinutesToHoursAndMinutes(CONTROL_SLEEP_TIME_IN_MINUTES)}
        metricStatus={
          hasChartData
            ? (patientMetrics?.totalSleepTimeInMinutes ?? CONTROL_SLEEP_TIME_IN_MINUTES) < CONTROL_SLEEP_TIME_IN_MINUTES
              ? VitalSignsStatus.ABNORMAL
              : VitalSignsStatus.NORMAL
            : undefined
        }
        hasRowLine={false}
      />
      <GridRow
        label={getLabel('REM')}
        metricValue={hasChartData ? formatMinutesToHoursAndMinutes(patientMetrics?.remSleepTimeInMinutes ?? 0) : '-'}
        normalControlValue={getFixedPointDisplay(controlLevel?.normalMaxRate)}
        hasRowLine={false}
        alignCenter
        rowHeaderClassName={style.remText}
        rowValueTextClassName={style.valueText}
      />
      <GridRow
        label={getLabel('Deep')}
        metricValue={hasChartData ? formatMinutesToHoursAndMinutes(patientMetrics?.deepSleepTimeInMinutes ?? 0) : '-'}
        normalControlValue={getFixedPointDisplay(controlLevel?.normalMaxRate)}
        hasRowLine={false}
        alignCenter
        rowHeaderClassName={style.deepText}
        rowValueTextClassName={style.valueText}
      />
      <GridRow
        label={getLabel('Light')}
        metricValue={hasChartData ? formatMinutesToHoursAndMinutes(patientMetrics?.lightSleepTimeInMinutes ?? 0) : '-'}
        normalControlValue={getFixedPointDisplay(controlLevel?.normalMaxRate)}
        hasRowLine={false}
        alignCenter
        rowHeaderClassName={style.lightText}
        rowValueTextClassName={style.valueText}
      />
      <GridRow
        label={getLabel('Awake')}
        metricValue={hasChartData ? formatMinutesToHoursAndMinutes(patientMetrics?.awakeTimeInMinutes ?? 0) : '-'}
        normalControlValue={getFixedPointDisplay(controlLevel?.normalMaxRate)}
        alignCenter
        rowHeaderClassName={style.awakeText}
        rowValueTextClassName={style.valueText}
      />
      {/*
      Motion data is hidden in the current version
      <GridRow
        label={getLabel('Motion')}
        metricValue={getFixedPointDisplay(patientMetrics?.motionSleepTimeInMinutes)}
        normalControlValue={getFixedPointDisplay(controlLevel?.normalMaxRate)}
        alignCenter
        rowHeaderClassName={style.motionText}
        rowValueTextClassName={style.valueText}
      />*/}
    </Grid>
  );
};
